import { Button, Space, Table, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useDeleteVenue } from "../../entities/api";
import { useFetchVenuesPagination } from "../../entities/api/useVenuesAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import { useHandlePagination } from "../../hooks/useHandlePagination";

const PermissionName = PermissionNames.VENUE;

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "venue.id",
        defaultMessage: "Venue Id",
      }),
      dataIndex: "venueId",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "timezone",
        defaultMessage: "timezone",
      }),
      dataIndex: "timezone",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "country",
        defaultMessage: "country",
      }),
      dataIndex: "countryCode",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/venues/manage/${record.venueId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const VenuesList = ({ currentVenues, columns = null }) => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useGetOrgData();
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { mutate: deleteFn, isPending } = useDeleteVenue();

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(50);

  const { data: venuesData, isLoading } = useFetchVenuesPagination({
    limit: pageSize,
    offset,
    nameLocalContains: searchText,
  });

  const data = useMemo(() => venuesData?.data || [], [venuesData?.data]);
  const nextLink = useMemo(() => venuesData?.links?.next, [venuesData?.links?.next]);

  useEffect(() => {
    let filteredData = data;
    if (currentVenues?.length) {
      filteredData = filteredData.filter(
        (venue) => !currentVenues.some((currentVenue) => venue.venueId === currentVenue.venueId),
      );
    }
    const filtered = filteredData;
    setFilteredData(filtered);
  }, [data, setFilteredData, currentVenues]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.venueId);
  }, [selected]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className={"mb-2"}
      >
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Person"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText="Delete"
          >
            <div>{`You are about to delete ${selected.nameLocal}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>Venues</Typography.Title>
        <Space>
          <SearchFilter doSearch={setSearchText} />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/venues/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                Venue
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </div>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={
          columns
            ? columns
            : columnsone({
                customerId,
                sport,
                organizationId,
                formatMessage,
                handleOpenConfirmation,
              })
        }
        rowKey={"venueId"}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + data.length + 1 : data.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
      />
    </>
  );
};

export default VenuesList;
